import { useRouter } from "next/router";
import { createPortal } from "react-dom";
import { useTranslationI18 } from "../../../../../services/hooks/useTranslationI18";
import { cn } from "../../../../../utils/cn";
import { OrderTypesEnum, PagesEnum } from "../../../../../utils/enums";
import { getPageLink } from "../../../../../utils/help/navigation/navigation";
import { Address, FranchiseDeliveryConfig } from "../../../../../utils/types";
import { PLink } from "../../../../global/PLink";
import { SavedLocationCard } from "../../../../shared/SavedLocationCard/SavedLocationCard";
import { SearchInput } from "../../../../shared/SearchInput/SearchInput";
import { Dropdown } from "../../Dropdown/MapsDropdown";
import { InputLocation } from "../../InputLocation/InputLocation";
import { MapCtaButtons } from "../../MapCtaFooter/MapCtaButtons";
import { LoginIcon } from "../../Map.icons";
import { useSearchBox } from "../useSearchBox";
import styles from "./SearchBoxV2.module.scss";

// Types
interface SearchBoxProps {
  setCenter: (center: { lat: number; lng: number }) => void;
  center: { lat: number; lng: number };
  setZoom: (zoom: number) => void;
  detailsSection: boolean;
  setDetailsSection: (bol: boolean) => void;
  setError: (value: { text: string; success?: boolean } | null) => void;
  zoom: number;
  /** When provided, the search input + dropdown will be rendered inside this container (e.g. over the map) */
  searchInputContainer?: HTMLDivElement | null;
  /** Skip button - shown beside confirm when provided and hasNoSkipBtn is false */
  onSkip?: () => void;
  skipTranslation?: string;
  hasNoSkipBtn?: boolean;
  /** When provided, called after a successful save-address form submit instead of navigating to home */
  onSaveAddressSuccess?: () => void;
  /** V3: override the delivery type ID used to filter saved addresses (active tab instead of currentDeliveryConfig) */
  activeDeliveryTypeId?: number;
  /** V3: the full delivery config for the active tab — dispatched to Redux on confirm */
  activeDeliveryConfig?: FranchiseDeliveryConfig | null;
}

export const SearchBoxV2 = (props: SearchBoxProps) => {
  const router = useRouter();
  const {
    searchInputContainer,
    onSkip,
    skipTranslation,
    hasNoSkipBtn,
    activeDeliveryTypeId,
  } = props;
  const {
    delivery,
    currentDeliveryConfig,
    displayLogin,
    disableSaveAddressButton,
    disableStreetNameInput,
    selectedAddressState,
    restaurantConfig,
    enableInternalMaps,
    enableNewDeliveryExperience,
    isMainDark,
    zoom,
    detailsSection,
    ref,
    finalReady,
    finalValue,
    finalStatus,
    finalData,
    handleInput,
    handleSelect,
    handleSelectSavedLocations,
    handleConfirm,
    handleChangeInput,
    handleSubmitNewAddress,
    handleLoginClick,
    reportSkippedAddressForm,
    handleModal,
    checkActiveColumn,
    validateInput,
    // getCurrentPosition,
    translations,
  } = useSearchBox(props);

  const { t } = useTranslationI18();
  const noResultsTranslation = t("delivery:no-saved-addresses");

  // Render input fields for address form
  const renderAddressForm = () => {
    return (
      <form onSubmit={handleSubmitNewAddress}>
        {checkActiveColumn("area") && (
          <InputLocation
            name="area"
            handleChange={handleChangeInput}
            validateInput={validateInput}
            value={delivery.selectedAddress?.area}
            label={translations.area}
            required={true}
            disabled={restaurantConfig?.addressFormAutoFill ?? false}
          />
        )}
        {checkActiveColumn("street_name") && (
          <InputLocation
            name="streetName"
            handleChange={handleChangeInput}
            validateInput={validateInput}
            value={delivery.selectedAddress?.streetName}
            label={translations.streetName}
            required={true}
            disabled={disableStreetNameInput}
          />
        )}
        {checkActiveColumn("building_number") && (
          <InputLocation
            name="buildingNumber"
            handleChange={handleChangeInput}
            validateInput={validateInput}
            value={delivery.selectedAddress?.buildingNumber}
            label={translations.buildingNumber}
            required={true}
          />
        )}
        {checkActiveColumn("floor_number") && (
          <InputLocation
            name="floorNumber"
            handleChange={handleChangeInput}
            validateInput={validateInput}
            value={delivery.selectedAddress?.floorNumber}
            label={translations.floorNumber}
            required={true}
          />
        )}
        {checkActiveColumn("apartment_number") && (
          <InputLocation
            name="apartmentNumber"
            handleChange={handleChangeInput}
            validateInput={validateInput}
            value={delivery.selectedAddress?.apartmentNumber}
            label={translations.apartmentNumber}
            required={true}
          />
        )}
        {checkActiveColumn("additional_details") && (
          <InputLocation
            name="additionalDetails"
            handleChange={handleChangeInput}
            validateInput={validateInput}
            value={delivery.selectedAddress?.additionalDetails}
            label={translations.additionalDetails}
            required={false}
          />
        )}
        <InputLocation
          name="addressTitle"
          handleChange={handleChangeInput}
          validateInput={validateInput}
          value={delivery.selectedAddress?.addressTitle}
          label={translations.addressTitle}
          required={true}
          placeholder={translations.addressTitlePlaceholder}
        />
        <button
          type="submit"
          disabled={disableSaveAddressButton}
          className={styles.save_btn}
          data-testid={"save-address"}
        >
          {translations.saveAddress}
        </button>
      </form>
    );
  };

  // Render saved addresses list
  const renderSavedAddresses = () => {
    const effectiveDeliveryTypeId =
      activeDeliveryTypeId ?? currentDeliveryConfig?.deliveryType?.id;
    const filteredAddresses = delivery.savedAddresses.filter(
      (address: Address) =>
        address.deliveryTypeId === effectiveDeliveryTypeId ||
        !effectiveDeliveryTypeId
    );

    return filteredAddresses.length === 0 ? (
      <div className={styles.no_saved_addresses}>
        <p>{noResultsTranslation}</p>
      </div>
    ) : (
      filteredAddresses.map((address: Address) => (
        <SavedLocationCard
          key={address.id}
          title={address.addressTitle}
          details={
            address.streetName
              ? `${address.streetName}, ${address.area}`
              : undefined
          }
          isSelected={
            !!(
              delivery.selectedAddress &&
              selectedAddressState?.id === address.id
            )
          }
          onSelect={() => handleSelectSavedLocations(address)}
          onDelete={() =>
            handleModal(
              true,
              "DeleteAddressConfirmation/DeleteAddressConfirmation",
              { componentProps: { deliveryAddress: address } }
            )
          }
          testId="address-details"
          titleTestId={`address-title-${address.id}`}
        />
      ))
    );
  };

  // Search input element - rendered in place or portaled into map overlay
  const searchInputElement = (
    <SearchInput
      containerRef={ref}
      value={finalValue}
      onChange={handleInput}
      disabled={enableInternalMaps ? false : !finalReady}
      placeholder={translations.searchBarText}
      testId="address-search-box"
    >
      {finalStatus === "OK" && (
        <Dropdown handleSelect={handleSelect} data={finalData} />
      )}
    </SearchInput>
  );

  // Render search box and related components
  const renderSearchBox = () => {
    return (
      <div className={styles.search_box_content}>
        {searchInputContainer
          ? createPortal(searchInputElement, searchInputContainer)
          : searchInputElement}
        {displayLogin && (
          <PLink
            link={getPageLink(router, PagesEnum.Login, {
              queryParams: ["returnTo=location"],
              // V3's delivery tab is local state on the location screen, so
              // without this sign-in returned with the order type the screen
              // was opened with and landed on Pick up (prepits/project#4344).
              // DELIVERYV2, not delivery_type_<id>: Login treats the latter as
              // an invalid order type and forces takeout.
              orderType:
                activeDeliveryTypeId !== undefined
                  ? OrderTypesEnum.DELIVERYV2
                  : undefined,
            })}
            onClick={handleLoginClick}
          >
            <div className={styles.saved_location}>
              <div className={styles.login_icon}>
                <LoginIcon size={24} />
              </div>
              <b>{translations.login} </b>
              &nbsp; {translations.toViewAddress}
            </div>
          </PLink>
        )}

        {!displayLogin && (
          <div className={styles.locations}>{renderSavedAddresses()}</div>
        )}
        <div className={styles.button_wrapper}>
          <MapCtaButtons
            skipLabel={!hasNoSkipBtn && onSkip ? skipTranslation : undefined}
            skipTestId="skip-button"
            onSkip={
              !hasNoSkipBtn && onSkip
                ? () => {
                    reportSkippedAddressForm();
                    onSkip?.();
                  }
                : undefined
            }
            primaryLabel={translations.confirmAddress}
            primaryTestId="confirm-address"
            onPrimary={handleConfirm}
            primaryDisabled={zoom <= 14 && enableNewDeliveryExperience}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className={cn(
        detailsSection ? styles.details_div : styles.search_box_div,
        isMainDark && "invert"
      )}
    >
      {!displayLogin && !detailsSection && (
        <p className={styles.saved_addresses_title}>
          {translations.savedAddresses}
        </p>
      )}
      {/* {!enableNewDeliveryExperience && (
        <div className={styles.detect_location} onClick={getCurrentPosition}>
          <DetectLocationIcon /> {translations.detectLocation}
        </div>
      )} */}
      {/* {orderTypeParam === OrderTypesEnum.DELIVERYV2 &&
        franchiseConfig?.deliveryV2 &&
        franchiseConfig?.deliveryV2.filter(
          (deliveryConfig: FranchiseDeliveryConfig) =>
            deliveryConfig.usesMap === true
        ).length > 1 &&
        !detailsSection && (
          <div className={styles.delivery_drop_down_wrapper}>
            <p className={styles.delivery_type_p}>
              {translations.deliveryType}
            </p>
            <OrderTypeToggle deliveryDropDown={true} />
          </div>
        )} */}

      {detailsSection ? (
        <div className={styles.form_wrapper} data-testid="form-drawer">
          {renderAddressForm()}
        </div>
      ) : (
        renderSearchBox()
      )}
    </div>
  );
};
